/**
 * Helper functions for coach availability response page components.
 * Shared across multiple sub-components in this directory.
 */

import { parseDateOnly } from '../../../utils/dateHelpers';
import type { AvailabilityRequestWithDetails } from '../../../services/api/coachPortal';

/** Default slot window length (minutes) when no configured duration is available. */
const DEFAULT_SESSION_DURATION_MINUTES = 60;

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'TBD';
  const date = parseDateOnly(dateStr);
  if (!date) return 'TBD';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate) return 'TBD';
  const start = parseDateOnly(startDate);
  if (!start) return 'TBD';
  if (!endDate) return formatDate(startDate);
  const end = parseDateOnly(endDate);
  if (!end) return formatDate(startDate);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export function getModalityLabel(modality: string): string {
  const labels: Record<string, string> = {
    group_coaching: 'Group Coaching',
    one_on_one: '1:1 Coaching',
    workshop: 'Workshop',
  };
  return labels[modality] || modality;
}

/**
 * MED-1026: Derive the session duration (in minutes) used to render each
 * time-slot window on the coach availability response page.
 *
 * The time slots belong to the GROUP COACHING modality, so each slot's window
 * length must reflect that modality's configured `session_duration_minutes`.
 * We deliberately do NOT use the flattened cohort-wide `session_duration_minutes`
 * scalar: on a mixed-modality cohort that value is mis-derived to the 1:1
 * duration (e.g. 45 min) rather than the group-coaching duration (e.g. 75 min).
 * The per-modality breakdown was surfaced into the payload by MED-1025 (MDF-142).
 *
 * Fallback chain (only when no group-coaching duration is present): the
 * flattened scalar, then a 60-minute default — preserving the prior behavior
 * for the degenerate case rather than rendering a blank or zero-length window.
 */
export function getGroupCoachingDurationMinutes(
  request: AvailabilityRequestWithDetails,
): number {
  const groupCoaching = request.modality_details.find(
    m => m.modality_type === 'group_coaching',
  );
  return (
    groupCoaching?.session_duration_minutes ??
    request.session_duration_minutes ??
    DEFAULT_SESSION_DURATION_MINUTES
  );
}
